import { CALM_COURSE_PALETTE, colorLuminance, contrastRatio } from './calmCourseColors'
import { effectiveCourseMeetings, enrollmentIdentity } from './courseColorSchedule'

export const SMART_PALETTE_VERSION = 'calm-8-v1'
export const SMART_COURSE_PALETTE = [...CALM_COURSE_PALETTE,
  { id: 'calm-peach', label: '피치', bg: '#EBCBB5', text: '#182230', meta: '#475467', border: '#AAB5C2' },
  { id: 'calm-mist', label: '미스트', bg: '#BFDDE5', text: '#182230', meta: '#475467', border: '#AAB5C2' },
]
// Section locator, not subjectKey: different sections must remain independent.
export const courseColorKey = course => enrollmentIdentity(course.sourceCourse ?? course)
export const storedCourseColor = course => course.color ?? course.backgroundColor ?? course.bg
const paletteFor = value => SMART_COURSE_PALETTE.find(p => p.id === value || p.bg.toLowerCase() === String(value).toLowerCase())
const hash = key => [...key].reduce((n,c) => (n * 31 + c.charCodeAt(0)) >>> 0, 0)
const matrices = [
  [[.152286,1.052583,-.204868],[.114503,.786281,.099216],[-.003882,-.048116,1.051998]],
  [[.367322,.860646,-.227968],[.280085,.672501,.047413],[-.01182,.04294,.968881]],
  [[1.255528,-.076749,-.178779],[-.078411,.930809,.147602],[.004733,.691367,.3039]],
]
function linear(hex) { return [1,3,5].map(i => parseInt(hex.slice(i,i+2),16)/255).map(v => v <= .04045 ? v/12.92 : ((v+.055)/1.055)**2.4) }
function lab(rgb) {
  const xyz = [[.4124564,.3575761,.1804375],[.2126729,.7151522,.072175],[.0193339,.119192,.9503041]].map((r,i) => r.reduce((n,v,j)=>n+v*rgb[j],0)/[.95047,1,1.08883][i])
  const f=xyz.map(v=>v>(6/29)**3?Math.cbrt(v):v/(3*(6/29)**2)+4/29)
  return [116*f[1]-16,500*(f[0]-f[1]),200*(f[1]-f[2])]
}
export function colorSeparation(a,b, { simulate=true }={}) {
  if (!/^#[0-9a-f]{6}$/i.test(a) || !/^#[0-9a-f]{6}$/i.test(b)) return 30
  const x=linear(a),y=linear(b)
  return Math.min(...[null,...(simulate ? matrices : [])].map(matrix => {
    const transform = rgb => matrix ? matrix.map(row=>Math.min(1,Math.max(0,row.reduce((n,v,j)=>n+v*rgb[j],0)))) : rgb
    const p=lab(transform(x)),q=lab(transform(y))
    return Math.hypot(...p.map((v,i)=>v-q[i]))
  }))
}
function proximity(a,b) {
  let score=0
  for(const x of effectiveCourseMeetings(a)) for(const y of effectiveCourseMeetings(b)) {
    const gap=Math.max(x.startMinute,y.startMinute)-Math.min(x.endMinute,y.endMinute)
    if(x.weekday===y.weekday && gap<=30) score=Math.max(score,3)
    else if(x.weekday===y.weekday && gap<=120) score=Math.max(score,1)
    else if(Math.abs(x.weekday-y.weekday)===1 && gap<0) score=Math.max(score,2)
  }
  return score
}
// Pure preparation at mutation boundaries. Existing colors are immutable by default.
// Unknown legacy sources are treated as manual; no automatic migration or cloud write.
export function prepareSmartCourseColors(courses, previous=[], { reorganize=false, legacyDefaults=new Map() }={}) {
  const prior=new Map(previous.map(c=>[courseColorKey(c),c]))
  const groups=new Map()
  for(const course of courses) {
    const key=courseColorKey(course)
    if(!groups.has(key)) groups.set(key,{key,course,rows:[]})
    groups.get(key).rows.push(course)
  }
  const usage=Array(8).fill(0),assigned=[]
  for(const group of groups.values()) {
    const current=group.course,old=prior.get(group.key)
    let value=storedCourseColor(current),source=current.colorSource
    if(!value && old && storedCourseColor(old) && !current.resetAutoColor) { value=storedCourseColor(old);source=old.colorSource }
    if(!value && old && !current.resetAutoColor && legacyDefaults.get(old.id)) { value=legacyDefaults.get(old.id).bg;source='auto' }
    if(value && !(reorganize && source==='auto')) {
      group.value=value;group.source=source ?? 'manual';group.fixed=true
      const p=paletteFor(value);if(p) usage[SMART_COURSE_PALETTE.indexOf(p)]++
      assigned.push(group)
    }
  }
  for(const group of [...groups.values()].filter(g=>!g.fixed).sort((a,b)=>a.key.localeCompare(b.key))) {
    const unused=SMART_COURSE_PALETTE.map((_,i)=>i).filter(i=>usage[i]===0)
    const indices=unused.length?unused:SMART_COURSE_PALETTE.map((_,i)=>i)
    const offset=hash(group.key)%8
    const candidates=indices.map(index=>({index,score:usage[index]*2+assigned.reduce((sum,other)=>{
      const otherHex=paletteFor(other.value)?.bg ?? other.value
      return sum+proximity(group.course,other.course)*(paletteFor(other.value)?.id===SMART_COURSE_PALETTE[index].id?100:Math.max(0,18-colorSeparation(SMART_COURSE_PALETTE[index].bg,otherHex)))
    },0),tie:(index-offset+8)%8})).sort((a,b)=>a.score-b.score||a.tie-b.tie)
    group.value=SMART_COURSE_PALETTE[candidates[0].index].bg;group.source='auto';usage[candidates[0].index]++;assigned.push(group)
  }
  return courses.map(course=>{
    const group=groups.get(courseColorKey(course))
    if(group.fixed && storedCourseColor(course)) return course
    const rest={...course};delete rest.resetAutoColor
    return {...rest,color:group.value,backgroundColor:group.value,colorSource:group.source,paletteVersion:SMART_PALETTE_VERSION}
  })
}
export const smartPaletteContrast = () => SMART_COURSE_PALETTE.map(p=>({...p,luminance:colorLuminance(p.bg),primaryContrast:contrastRatio(p.bg,p.text),secondaryContrast:contrastRatio(p.bg,p.meta)}))

export function inheritReplacementColor(replacement, previous, legacyDefault) {
  if (replacement.colorSource === 'manual' || !previous) return replacement
  const fields = ['color', 'backgroundColor', 'bg', 'textColor', 'metaColor', 'borderColor', 'colorSource', 'paletteVersion']
  const next = { ...replacement }
  for (const key of fields) { delete next[key]; if (previous[key] != null) next[key] = previous[key] }
  if (!storedCourseColor(previous) && legacyDefault) {
    next.color = legacyDefault.bg; next.backgroundColor = legacyDefault.bg; next.colorSource = 'auto'; next.paletteVersion = SMART_PALETTE_VERSION
  }
  return next
}
